import React, { useState, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import EnhancedPropertyCard from '../EnhancedPropertyCard';
import { getFeaturedProperties } from '../../services/propertyService';
import { useNavigate } from 'react-router-dom';
import Toast from "../Toast";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import '../../assets/css/property.css';
import NoPropertiesAnimation from '../common/NoPropertiesAnimation';

const NextArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <ArrowForwardIosIcon
      className={className}
      style={{
        ...style,
        display: "block",
        color: "#d8b035",
        fontSize: '30px',
        right: '-40px'
      }}
      onClick={onClick}
    />
  );
};

const PrevArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <ArrowBackIosIcon
      className={className}
      style={{
        ...style,
        display: "block",
        color: "#d8b035",
        fontSize: '30px',
        left: '-35px'
      }}
      onClick={onClick}
    />
  );
};

const Property = ({ country }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toastOpen, setToastOpen] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState("");
  const [toastSeverity, setToastSeverity] = React.useState("info");
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    // Fetch only featured properties
    getFeaturedProperties({ country, limit: 6 })
      .then((res) => {
        if (res && res.length > 0) {
          setProperties(res);
        } else {
          setToastMessage("No featured properties found.");
          setToastSeverity("info");
          setToastOpen(true);
        }
      })
      .catch((err) => {
        console.error('Error fetching featured properties:', err);
        setToastMessage("Failed to load featured properties. Please try again.");
        setToastSeverity("error");
        setToastOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [country]);

  const handleShowMore = () => {
    navigate(`/properties?featured=true&country=${country}`);
  };

  const carouselSettings = {
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 1200, // Show 3 slides but with adjusted padding for larger screens
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          nextArrow: <NextArrow />,
          prevArrow: <PrevArrow />
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          nextArrow: <NextArrow />,
          prevArrow: <PrevArrow />
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: true,
          dots: true,
          nextArrow: <NextArrow />,
          prevArrow: <PrevArrow />
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          centerMode: false,
        },
      },
    ],
  };

  return (
    <div className="propartes-area" style={{ paddingTop: '30px', paddingBottom: '30px', paddingLeft: '40px', paddingRight: '40px' }}>
      <div className="container">
        <div className="section-title text-center">
          <h2 style={{ fontWeight: '700', fontSize: '1.7rem', color: '#333' }}>Amaravati Real Estate – Premium Open Plots for Sale by Royale Spaces</h2>
          <p style={{ fontWeight: '700', fontSize: '1.3rem', color: '#333' }}>Secure your future with clear-title residential and commercial plots in Amaravati</p>
        </div>

        {/* Conditional rendering of carousel or grid */}
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #d8b035',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }}></div>
              <p>Loading featured properties...</p>
            </div>
          </Box>
        ) : properties.length > 0 ? (
          properties.length > 3 ? (
            <Slider {...carouselSettings}>
              {properties.map((property, index) => (
                <Box
                  key={property._id || index}
                  sx={{
                    padding: 1
                  }}
                >
                  <EnhancedPropertyCard property={property} />
                </Box>
              ))}
            </Slider>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
              {properties.map((property, index) => (
                <div
                  key={property._id || index}
                  style={{
                    flex: '0 0 auto',
                    width: '100%',
                    maxWidth: '400px'
                  }}
                >
                  <EnhancedPropertyCard property={property} />
                </div>
              ))}
            </div>
          )
        ) : (
          <NoPropertiesAnimation type="featured" />
        )}

        {/* Show More Button */}
        <Box textAlign="center" marginTop={5}>
          {properties.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleShowMore}
              sx={{
                backgroundColor: '#d8b035',
                color: '#fff',
                fontWeight: '600',
                padding: '10px 20px',
                marginBottom: '15px',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#c19b2e',
                  transform: 'scale(1.05)',
                  transition: 'all 0.3s ease',
                },
              }}
            >
              View All Featured Properties
            </Button>
          )}
        </Box>
      </div>
      <Toast open={toastOpen} message={toastMessage} severity={toastSeverity} onClose={() => setToastOpen(false)} />
    </div>
  );
};

export default Property;